<template>
  <div class="insights-smart-notifications-card">
    <section class="header">
      <h5>{{ rule.name }}</h5>
      <section class="header-description">
        <div class="header-item">
          <i class="hi hi-info"></i>
          <div class="ellipsis">
            <span class="text-capitalize">{{ rule.frequency }}</span>
            {{ isGeoIpListMatch ? geoIpListCardSummary : ruleSubtitle }}
          </div>
        </div>
        <div class="header-item mt-5">
          <i class="hi hi-bell"></i>
          <span>
            {{ formTo }}
          </span>
        </div>
      </section>
    </section>
    <section class="body">
      <section
        v-for="(match, index) in baseMatches"
        :key="index"
        class="member"
      >
        <AppAvatar
          :avatar="match.avatar"
          size="speck"
        />
        <div class="member-info">
          <div class="d-flex justify-space-between">
            <span class="member-name">{{ match.name }}</span>
            <div class="member-value">
              <span>{{ getConditionValue(match.value).value }}</span>
              {{ ' ' }}
              <span>{{ getConditionValue(match.value).addon }}</span>
              <a
                v-if="isCountMetric"
                v-tooltip="'View audit log'"
                :href="auditLogUrl(match.user_id)"
                target="_blank"
                class="d-inline-flex align-items-center insights-view-full-day-link gap-5 ml-10"
                @click="trackAuditLogClick"
              >
                <i class="hi hi-external"></i>
              </a>
              <InsightsViewFullDayLink
                v-else-if="showFullDayLink(rule.frequency)"
                :date="notification.from"
                :organization-id="organization.id"
                :user-id="match.user_id"
                class="ml-10"
              />
            </div>
          </div>
        </div>
      </section>
    </section>
    <section
      v-if="viewAll.show"
      class="footer"
    >
      <span
        class="cta"
        @click="openDialog"
      >
        View all {{ viewAll.count }} members
      </span>
    </section>
  </div>
</template>

<script>
import AppAvatar from '~/components/AppAvatar.vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate, formatDatetime, parseInternalDatetime } from '~/utils/time_utils';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { mapState } from 'vuex';
import InsightsViewFullDayLink from '~/components/insights/InsightsViewFullDayLink.vue';
import SmartNotificationMixin from '~/mixins/Insights/SmartNotificationMixin';

// Don't forget to fix the carousel height if you change this constant.
// Carousel height is hardcoded;
const MATCHES_PER_CARD = 5;

dayjs.extend(utc);

export default {
  components: {
    AppAvatar,
    InsightsViewFullDayLink,
  },
  mixins: [SmartNotificationMixin],
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MATCHES_PER_CARD,
    };
  },
  computed: {
    ...mapState(['organization']),
    rule() {
      return this.notification.snapshot;
    },
    formTo() {
      if (this.rule.frequency === 'hourly') {
        return formatDatetime(parseInternalDatetime(this.notification.from));
      }

      return formatDate(parseInternalDatetime(this.notification.from));
    },
    baseMatches() {
      return this.notification.matches.slice(0, MATCHES_PER_CARD);
    },
    isTimeSpentInTool() {
      return this.rule.metric_type === 'time_spent_in_tool';
    },
    computedMetricType() {
      const metricTypeDict = {
        application: 'time_spent_in_app',
        url: 'time_spent_in_url',
      };

      if (!this.isTimeSpentInTool) {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][this.rule.metric_type];
      }

      if (this.rule.data && this.rule.data.tools) {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][
          metricTypeDict[this.rule.data.tools.type]
        ];
      }

      return '';
    },
    viewAll() {
      // Tool and geo-IP list-match rules always expose the dialog so the configured
      // app/URL/IP/city/country list is reachable, even with few member matches.
      const minimumLength = (this.isTimeSpentInTool || this.isGeoIpListMatch) ? 0 : MATCHES_PER_CARD;

      return {
        show: this.notification.matches.length > minimumLength,
        count: this.notification.matches.length,
      };
    },
  },
  methods: {
    openDialog() {
      this.$emit('openDialog', this.notification);
    },
  },
};
</script>
